/**
 * Bitácora FC - GraphQL (urql) conectado al backend
 */
import { useCallback } from 'react';
import { useQuery, useMutation } from './urqlHooks';

export const GET_BITACORAS = `
  query GET_BITACORAS($companyId: ID!, $statusFilter: String) {
    bitacoras(input: { companyId: $companyId, statusFilter: $statusFilter }) {
      id
      companyId
      flowDate
      title
      status
      deliveredBy
      deliveredByEmail
      deliveredAt
      imageWithProjectionsUrl
      imageWithoutProjectionsUrl
      imageBankAccountsUrl
      bankBalances { bankName initialBalance label }
      initialComments
      emailDraft
      comments { user { id name email } date comment source }
      createdByEmail
      createdAt
      updatedByEmail
      updatedAt
      editionHistory {
        date
        userId
        userEmail
        field
        event
      }
    }
  }
`;

const CREATE_BITACORA = `
  mutation CREATE_BITACORA($input: CreateLogbookInput!) {
    createBitacora(input: $input) {
      id
      companyId
      flowDate
      title
      status
      initialComments
      emailDraft
      imageWithProjectionsUrl
      imageWithoutProjectionsUrl
      imageBankAccountsUrl
      bankBalances { bankName initialBalance label }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_BITACORA = `
  mutation UPDATE_BITACORA($id: ID!, $input: UpdateLogbookInput!) {
    updateBitacora(id: $id, input: $input) {
      id
      flowDate
      title
      status
      initialComments
      emailDraft
      imageWithProjectionsUrl
      imageWithoutProjectionsUrl
      imageBankAccountsUrl
      bankBalances { bankName initialBalance label }
      updatedAt
    }
  }
`;

const SEND_BITACORA = `
  mutation SEND_BITACORA($input: SendLogbookInput!) {
    sendBitacora(input: $input) {
      id
      status
      deliveredBy
      deliveredByEmail
      deliveredAt
    }
  }
`;

const DELETE_BITACORA = `
  mutation DELETE_BITACORA($id: ID!) {
    deleteBitacora(id: $id) {
      id
    }
  }
`;

const CREATE_BITACORA_COMMENT = `
  mutation CREATE_BITACORA_COMMENT($logbookId: ID!, $input: CreateLogbookCommentInput!) {
    createBitacoraComment(logbookId: $logbookId, input: $input) {
      id
      comments { user { id name email } date comment source }
    }
  }
`;

const ANALIZAR_FLUJO = `
  mutation ANALIZAR_FLUJO($input: AnalyzeCashflowInput!) {
    analizarFlujo(input: $input) {
      analysis
      emailDraft
    }
  }
`;

const CHAT_BITACORA_ITERATIVO = `
  mutation CHAT_BITACORA_ITERATIVO($input: ChatBitacoraIterativoInput!) {
    chatBitacoraIterativo(input: $input) {
      reply
    }
  }
`;

export const useBitacoras = (companyId) => {
  const hasValidCompany = Boolean(companyId && String(companyId).trim());
  const [result, reexecuteQuery] = useQuery({
    query: GET_BITACORAS,
    variables: {
      companyId: hasValidCompany ? String(companyId).trim() : '',
      statusFilter: null,
    },
    pause: !hasValidCompany,
    requestPolicy: 'cache-and-network',
  });

  const stableReexecute = useCallback(() => {
    if (hasValidCompany) reexecuteQuery({ requestPolicy: 'network-only' });
  }, [hasValidCompany, reexecuteQuery]);

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: stableReexecute,
  };
};

export const useCreateBitacora = () => {
  const [result, executeMutation] = useMutation(CREATE_BITACORA);
  return {
    fetching: result.fetching,
    error: result.error,
    executeMutation: async (input) => {
      const r = await executeMutation({ input });
      if (r.error) return { data: null, error: r.error };
      return { data: r.data, error: null };
    },
  };
};

export const useUpdateBitacora = () => {
  const [result, executeMutation] = useMutation(UPDATE_BITACORA);
  return {
    fetching: result.fetching,
    error: result.error,
    executeMutation: async (id, input) => {
      const r = await executeMutation({ id, input });
      if (r.error) return { data: null, error: r.error };
      return { data: r.data, error: null };
    },
  };
};

export const useSendBitacora = () => {
  const [result, executeMutation] = useMutation(SEND_BITACORA);
  return {
    fetching: result.fetching,
    error: result.error,
    executeMutation: async (id, emails) => {
      const r = await executeMutation({ input: { logbookId: id, emails } });
      if (r.error) return { data: null, error: r.error };
      return { data: r.data, error: null };
    },
  };
};

export const useCreateBitacoraComment = () => {
  const [result, executeMutation] = useMutation(CREATE_BITACORA_COMMENT);
  return {
    fetching: result.fetching,
    executeMutation: async (bitacoraId, input) => {
      const r = await executeMutation({ logbookId: bitacoraId, input });
      if (r.error) return { data: null, error: r.error };
      return { data: r.data, error: null };
    },
  };
};

export const useDeleteBitacora = () => {
  const [result, executeMutation] = useMutation(DELETE_BITACORA);
  return {
    fetching: result.fetching,
    error: result.error,
    executeMutation: async (bitacoraId) => {
      const r = await executeMutation({ id: bitacoraId });
      if (r.error) return { data: null, error: r.error };
      return { data: r.data, error: null };
    },
  };
};

export const analyzeBitacoraFlow = async (input) => {
  const graphqlUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/graphql';
  const token = localStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(graphqlUrl, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      query: ANALIZAR_FLUJO,
      variables: { input },
    }),
  });

  const json = await response.json().catch(() => ({}));
  const data = json?.data?.analizarFlujo;
  const errors = json?.errors;

  if (errors?.length || !data?.analysis) {
    const message = errors?.[0]?.message || json?.errors?.[0]?.message || 'No se pudo generar el análisis con IA.';
    throw new Error(message);
  }

  return { success: true, analysis: data.analysis, emailDraft: data.emailDraft || '' };
};

export const chatBitacoraIterativo = async (logbookId, messages = []) => {
  const graphqlUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/graphql';
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(graphqlUrl, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      query: CHAT_BITACORA_ITERATIVO,
      variables: { input: { logbookId, messages } },
    }),
  });

  const json = await response.json().catch(() => ({}));
  const data = json?.data?.chatBitacoraIterativo;
  const errors = json?.errors;

  if (errors?.length || !data?.reply) {
    const message = errors?.[0]?.message || json?.errors?.[0]?.message || 'No se pudo obtener respuesta de la IA.';
    throw new Error(message);
  }

  return { success: true, reply: data.reply };
};
